/**
 * StandaloneMigrationCard — Admin → System tab.
 *
 * Shows the audit log of standalone Smile Design AI → PractCom data ports
 * (logos, transformations, consultations) and lets the founder either run
 * a single email manually or trigger a one-shot bulk backfill for every
 * customer who's both in `smile_practices` and `practices`.
 */
import React, { useEffect, useState } from 'react';
import {
  Database, Loader2, RefreshCw, AlertTriangle, Play, Users,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StandaloneMigrationCard({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(null);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/migrations`, { headers });
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const data = await res.json();
      setRows(data.migrations || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const runOne = async () => {
    if (!email.trim()) { setErr('Enter an email first'); return; }
    setBusy('one'); setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/migrations/run`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Run failed: ${res.status}`);
      setToast(`Migrated ${email}: ${data.transformationsMoved} transformations, ${data.consultationsMoved} consultations`);
      setTimeout(() => setToast(null), 4000);
      setEmail('');
      await load();
    } catch (e) { setErr(e.message); }
    finally     { setBusy(null); }
  };

  const backfill = async () => {
    if (!window.confirm('Run bulk backfill for every customer who has both a standalone Smile Design AI account and a PractCom account?')) return;
    setBusy('bulk'); setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/migrations/backfill`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Backfill failed: ${res.status}`);
      setToast(`Backfill complete: ${data.totalMigrated} migrated · ${data.totalAlready} already done`);
      setTimeout(() => setToast(null), 5000);
      await load();
    } catch (e) { setErr(e.message); }
    finally     { setBusy(null); }
  };

  return (
    <div data-testid="standalone-migration-card"
         className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="bg-emerald-600 text-white rounded-lg p-2.5">
          <Database className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-emerald-900">Standalone → PractCom Migration</h3>
          <p className="text-xs text-emerald-800 mt-0.5 max-w-3xl leading-snug">
            Runs automatically on every PractCom purchase via SamCart. Ports the
            customer's Smile Design AI logo, branding, saved transformations and
            consultation requests onto their new PractCom practice so they log
            in and see all their work — no empty account.
          </p>
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2"
             data-testid="migrations-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}
      {toast && (
        <div className="bg-emerald-100 border border-emerald-300 text-emerald-900 text-sm rounded-lg p-2"
             data-testid="migrations-toast">
          {toast}
        </div>
      )}

      {/* Manual run + backfill */}
      <div className="bg-white border border-emerald-200 rounded-lg p-3 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            placeholder="customer@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="flex-1 px-3 py-2 border-2 border-slate-200 rounded text-sm focus:border-emerald-500 outline-none"
            data-testid="migration-email-input"
          />
          <button
            onClick={runOne}
            disabled={busy === 'one' || !email.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="migration-run-one"
          >
            {busy === 'one' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Migrate this email
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={backfill}
            disabled={busy === 'bulk'}
            className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold px-4 py-2 rounded inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="migration-backfill"
          >
            {busy === 'bulk' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
            Bulk backfill all standalone customers
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="bg-white border-2 border-emerald-300 text-emerald-900 text-sm font-bold px-3 py-2 rounded inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="migration-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh log
          </button>
        </div>
      </div>

      {/* Audit log */}
      <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden"
           data-testid="migrations-table">
        <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 text-xs font-bold text-emerald-900 uppercase tracking-wider">
          Recent migrations ({rows.length})
        </div>
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-slate-500 text-center" data-testid="migrations-empty">
            No migrations yet. They'll appear here after the next SamCart PractCom purchase.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Customer</th>
                  <th className="px-3 py-2 text-right font-bold">Transformations</th>
                  <th className="px-3 py-2 text-right font-bold">Consults</th>
                  <th className="px-3 py-2 text-center font-bold">Status</th>
                  <th className="px-3 py-2 text-right font-bold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.id} data-testid={`migration-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.customerEmail}</td>
                    <td className="px-3 py-2 text-right font-bold">{r.transformationsMoved ?? 0}</td>
                    <td className="px-3 py-2 text-right font-bold">{r.consultationsMoved ?? 0}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={
                        r.status === 'completed' ? 'bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded font-bold'
                          : r.status === 'failed'  ? 'bg-rose-100 text-rose-900 px-2 py-0.5 rounded font-bold'
                          : 'bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-bold'
                      }>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
